import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from '@/helpers/translate';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import HofPlusIcon from '@/components/icons/hofplus';

/**
 * WEA-157 — Filtre "Hall Of Fame" (remise de palier de fidélité). Visible pour un CONNECTÉ NON-ABONNÉ
 * uniquement (visibilité gérée dans prodConf via requireAuth + hideForSubscriber, appliqués par le
 * gate de filters.js). Exclusif avec "Legend +" (un seul `hofFilter` actif à la fois).
 *
 * Actif -> graphql `hof:['hof']` : restreint aux espaces du programme HOF ; le prix de palier du
 * viewer est déjà appliqué côté back (DataByCategoryResolver.getHofDiscountPercent).
 */
const HofTier = forwardRef(({ onValid }, ref) => {
  const { t } = useTranslation();
  const active = useSearchZustandStore((state) => state.hofFilter === 'tier');
  const userData = useZusUserConnectedStore((state) => state.userData);
  const level = userData?.corpo?.effectiveFidelityLevel ?? userData?.corpo?.fidelityLevel;

  useImperativeHandle(ref, () => ({
    getValue: () => (useSearchZustandStore.getState().hofFilter === 'tier' ? 'tier' : null),
    getOutput: () => ({
      keySlug: 'hofdiscount',
      slug: 'hofdiscount',
      url: 'tier',
      values: 'tier',
      graphql: { hof: ['hof'] },
    }),
    clearValue: () => useSearchZustandStore.setState((state) => (
      state.hofFilter === 'tier' ? { hofFilter: null } : {}
    )),
    setIsOpenModal: () => null,
    isSameDefault: () => useSearchZustandStore.getState().hofFilter !== 'tier',
  }), []);

  const toggle = () => {
    useSearchZustandStore.setState((state) => ({
      hofFilter: state.hofFilter === 'tier' ? null : 'tier',
    }));
    onValid?.();
  };

  const label = level ? `${t('search:hof.title')} -${level}%` : t('search:hof.title');

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={toggle}
      className={`flex justify-center items-center w-full text-sm rounded-lg transition-all duration-200 py-[8px] px-[8px] group ${active ? 'text-white bg-purple hover:bg-purple/80' : 'text-white bg-purple-6 hover:bg-purple/70'}`}
    >
      <HofPlusIcon className={`mr-2 size-5 transition-colors duration-200 ${active ? 'text-white' : 'text-purple-4/70 group-hover:text-white'}`} />
      <span className="truncate whitespace-nowrap transition-colors duration-200">{label}</span>
    </button>
  );
});

export default HofTier;
